import React from 'react';
import { Alert, Button, Modal, message } from 'antd';
import { cancelOrderDeletion, requestOrderDeletion, ServiceOrder } from '../../api/serviceOrders';

// Двухстадийное удаление заказ-наряда (service_order_deletion.md):
// стадия 1 — пометка + сообщение, какие документы удалить вручную в 1С;
// стадия 2 — скрытие после ручного удаления (фоном). Запись не удаляется.

const extractError = (e: any): string => {
  const detail = e?.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg).filter(Boolean).join('; ') || 'Ошибка';
  return typeof detail === 'string' ? detail : 'Ошибка запроса';
};

// Пункт меню «Удалить» (оба вида). Подтверждение → пометка → результат.
export const requestDeletionFlow = (order: ServiceOrder, onDone: () => void): void => {
  Modal.confirm({
    title: `Удалить заказ-наряд № ${order.number || order.id}?`,
    content: (
      <div>
        <p>Заказ будет помечен на удаление и станет недоступен для изменений.</p>
        <p>Марки будут отвязаны, заявка выйдет из очереди.</p>
        <p><b>Документы в 1С удаляются вручную</b> — после пометки будет показан их список.</p>
      </div>
    ),
    okText: 'Пометить на удаление',
    okButtonProps: { danger: true },
    cancelText: 'Отмена',
    onOk: async () => {
      try {
        const resp = await requestOrderDeletion(order.id);
        if (resp.onec_documents.length > 0) {
          Modal.warning({
            title: 'Удалите документы в 1С вручную',
            width: 520,
            content: (
              <div>
                <p>Заказ помечен на удаление. В 1С остались документы — <b>обязательно удалите их вручную</b>:</p>
                <ul>
                  {resp.onec_documents.map((d) => (
                    <li key={d.kind}>{d.kind_name}{d.number ? ` № ${d.number}` : ''}</li>
                  ))}
                </ul>
                <p>Когда документы будут удалены в 1С, заказ исчезнет из списков автоматически.</p>
              </div>
            ),
            okText: 'Понятно',
            onOk: onDone,
          });
        } else {
          message.success('Заказ помечен на удаление. Связанных документов в 1С нет — заказ скроется автоматически.');
          onDone();
        }
      } catch (e: any) {
        Modal.error({ title: 'Удаление невозможно', content: extractError(e) });
      }
    },
  });
};

// «Отменить удаление» — снятие пометки из pending / восстановление из done
// (из done — только если все документы в 1С восстановлены, схема §1 rev. №3).
// Марки назад не возвращаются в обоих случаях.
export const cancelDeletionFlow = (order: ServiceOrder, onDone: () => void): void => {
  const isDone = order.deletion_state === 'done';
  Modal.confirm({
    title: isDone
      ? `Восстановить заказ № ${order.number || order.id}?`
      : `Отменить удаление заказа № ${order.number || order.id}?`,
    content: isDone
      ? 'Восстановление возможно, только если все документы в 1С восстановлены (снята пометка удаления). Отвязанные марки НЕ вернутся — добавьте их повторным сканированием.'
      : 'Заказ снова станет доступен для изменений. Отвязанные марки НЕ вернутся — добавьте их повторным сканированием.',
    okText: isDone ? 'Восстановить' : 'Отменить удаление',
    cancelText: 'Назад',
    onOk: async () => {
      try {
        await cancelOrderDeletion(order.id);
        message.success(isDone ? 'Заказ восстановлен' : 'Пометка на удаление снята');
        onDone();
      } catch (e: any) {
        if (isDone && e?.response?.status === 409) {
          Modal.warning({ title: 'Восстановление невозможно', width: 520, content: extractError(e), okText: 'Понятно' });
        } else {
          message.error(extractError(e));
        }
      }
    },
  });
};

// Баннер помеченного/удалённого заказа (оба вида): read-only + кнопка отмены/восстановления.
export const DeletionBanner: React.FC<{
  order: ServiceOrder;
  canWrite: boolean;
  onCancelled: () => void;
}> = ({ order, canWrite, onCancelled }) => {
  if (!order.deletion_state) return null;
  const when = order.deletion_requested_at
    ? new Date(order.deletion_requested_at).toLocaleString('ru-RU')
    : '';
  const isDone = order.deletion_state === 'done';
  return (
    <Alert
      type={isDone ? 'info' : 'error'}
      showIcon
      style={{ marginBottom: 12 }}
      message={
        isDone
          ? `Заказ удалён${when ? ` — ${when}` : ''}${order.deletion_requested_by ? ` (${order.deletion_requested_by})` : ''}`
          : `Заказ помечен на удаление${when ? ` — ${when}` : ''}${order.deletion_requested_by ? ` (${order.deletion_requested_by})` : ''}`
      }
      description={
        isDone
          ? 'Документы в 1С удалены. Заказ скрыт из списков и доступен только по прямой ссылке. Восстановление возможно после восстановления документов в 1С.'
          : 'Редактирование запрещено. Удалите связанные документы в 1С вручную — после этого заказ исчезнет из списков.'
      }
      action={
        canWrite ? (
          <Button size="small" onClick={() => cancelDeletionFlow(order, onCancelled)}>
            {isDone ? 'Восстановить' : 'Отменить удаление'}
          </Button>
        ) : undefined
      }
    />
  );
};
